import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Navigation, Clock, CloudRain, Zap } from "lucide-react";
import { GrayBase, useCounties, project, W, H, liveLoopFrames } from "@/components/broadcast/TvMap";

export function useRadarArrivals() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const load = () => api.get("/radar/counties").then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 120_000);
    return () => clearInterval(t);
  }, []);
  return data;
}

const clockFromEta = (validIso, etaMin) => {
  const base = validIso ? new Date(validIso) : new Date();
  const t = new Date((Number.isNaN(base.getTime()) ? Date.now() : base.getTime()) + etaMin * 60000);
  return t.toLocaleString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" });
};
const etaText = (m) => (m <= 2 ? "NOW" : `~${m} min`);

function ArrivalRow({ r, valid, i }) {
  const strong = r.label === "strong storm";
  const imminent = r.eta_min <= 2;
  return (
    <div className="flex items-center gap-3.5 rounded-[9px] bg-white/[0.045] px-3.5 py-[9px] ring-1 ring-white/10" data-testid={`arrival-row-${i}`}>
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px]" style={{ background: strong ? "#d90429" : "#2f7bed" }}>
        {strong ? <Zap className="h-5 w-5 text-white" strokeWidth={2.6} /> : <CloudRain className="h-5 w-5 text-white" strokeWidth={2.4} />}
      </span>
      <span className="flex-1 truncate font-oswald text-[32px] font-bold uppercase leading-none text-white">{r.town}</span>
      <span className="shrink-0 font-plex text-[22px] font-semibold text-white/75">{clockFromEta(valid, r.eta_min)}</span>
      <span className={`flex h-10 w-[132px] shrink-0 items-center justify-center whitespace-nowrap rounded-[6px] font-oswald text-[26px] font-black tabular-nums ${imminent ? "bg-[#d90429] text-white" : "bg-[#ffd60a] text-[#0a0f1c]"}`}>{etaText(r.eta_min)}</span>
    </div>
  );
}

function ArrivalMiniMap({ arrivals, motion }) {
  const counties = useCounties();
  const frame = liveLoopFrames();
  const latestKey = frame[frame.length - 1]?.key;
  const dots = arrivals.filter((a) => a.lat != null && a.lon != null);
  const lead = dots[0];
  const arrow = (() => {
    if (!lead || !motion?.bearing == null) return null;
    const p = project(lead.lat, lead.lon);
    const rad = ((motion.bearing || 0) * Math.PI) / 180;
    const len = 150;
    return { x1: p.x, y1: p.y, x2: p.x + Math.sin(rad) * len, y2: p.y - Math.cos(rad) * len };
  })();
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-full w-full" preserveAspectRatio="xMidYMid slice" data-testid="arrival-minimap">
      <GrayBase counties={counties} strokeWidth={2.4}>
        {latestKey && <image href={`${API}/radar/frame?mode=live&key=${latestKey}`} x="0" y="0" width={W} height={H} preserveAspectRatio="none" opacity="0.92" />}
        {arrow && (
          <g>
            <defs>
              <marker id="arr-head" markerWidth="5" markerHeight="5" refX="2.5" refY="2.5" orient="auto">
                <path d="M0,0 L5,2.5 L0,5 Z" fill="#ffd60a" />
              </marker>
            </defs>
            <line x1={arrow.x1} y1={arrow.y1} x2={arrow.x2} y2={arrow.y2} stroke="#ffd60a" strokeWidth="9" strokeLinecap="round" markerEnd="url(#arr-head)" opacity="0.95" />
          </g>
        )}
        {dots.map((r, i) => {
          const { x, y } = project(r.lat, r.lon);
          const strong = r.label === "strong storm";
          const first = i === 0;
          return (
            <g key={r.town}>
              {first && <circle cx={x} cy={y} r="26" fill="none" stroke="#ffd60a" strokeWidth="4" />}
              <circle cx={x} cy={y} r="13" fill={strong ? "#d90429" : "#2f7bed"} stroke="#fff" strokeWidth="3" />
            </g>
          );
        })}
      </GrayBase>
    </svg>
  );
}

export function StormArrivalSlide({ data }) {
  const arrivals = (data?.arrivals || []).slice(0, 10);
  const motion = data?.motion;
  if (!arrivals.length || !motion) return null;
  const toward = (motion.toward || "").toUpperCase();
  return (
    <div data-testid="storm-arrival-slide" className="relative h-full w-full overflow-hidden" style={{ background: "linear-gradient(140deg, #0c1628 0%, #0a0f1c 60%, #071019 100%)" }}>
      <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "repeating-linear-gradient(135deg, #fff 0 2px, transparent 2px 28px)" }} />
      <div className="relative flex h-full w-full flex-col px-[46px] py-[26px]">
        {/* Motion header */}
        <div className="flex items-center gap-4">
          <span className="flex h-15 w-15 items-center justify-center rounded-full bg-[#ffd60a]" style={{ width: 60, height: 60 }}>
            <Navigation className="h-8 w-8 text-[#0a0f1c]" strokeWidth={2.4} style={{ transform: `rotate(${motion.bearing || 0}deg)` }} fill="#0a0f1c" />
          </span>
          <div>
            <div className="font-oswald text-[18px] font-bold uppercase tracking-[0.24em] text-[#9fb2d4]">Storm Arrival Times</div>
            <div className="font-oswald text-[42px] font-black uppercase leading-none text-white">Tracking {toward} · {motion.speed_mph} mph</div>
          </div>
        </div>

        {/* Map + list */}
        <div className="mt-4 grid flex-1 grid-cols-[42%_1fr] gap-7 overflow-hidden">
          <div className="overflow-hidden rounded-[12px] ring-1 ring-white/15">
            <ArrivalMiniMap arrivals={arrivals} motion={motion} />
          </div>
          <div className="flex min-h-0 flex-col gap-2">
            <div className="mb-0.5 font-oswald text-[23px] font-black uppercase tracking-[0.18em] text-[#ffd60a]">Next Up</div>
            {arrivals.map((r, i) => <ArrivalRow key={r.town} r={r} valid={data.valid} i={i} />)}
          </div>
        </div>

        <div className="mt-2 flex items-center gap-2 font-plex text-[18px] font-medium tracking-wide text-white/50">
          <Clock className="h-5 w-5" /> Radar-estimated arrival of the next line of storms · times approximate
        </div>
      </div>
    </div>
  );
}
